'use client';

import { useState, useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { X } from 'lucide-react';
import { getLocaleByCode } from '@/shared/constants/locales';
import { useDetectedLocale } from '../lib/useDetectedLocale';
import { setLocaleCookie } from '@/shared/lib/locale-cookie';

/**
 * Non-blocking banner that suggests switching to the language the browser
 * actually asks for, whenever it differs from the locale currently rendered.
 *
 * Works in BOTH directions, including back to English: users carrying a
 * geo-derived NEXT_LOCALE cookie from before the Accept-Language-first fix
 * (e.g. an IN IP pinned to Hindi) would otherwise have no way out except the
 * settings screen. An explicit pick in the selector sets the dismissed flag,
 * so a deliberate choice is never second-guessed.
 */
export function LanguageSuggestionBanner() {
  const t = useTranslations('languageBanner');
  const tCommon = useTranslations('common');
  const currentLocale = useLocale();
  const detectedLocale = useDetectedLocale();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the banner
    const dismissed = localStorage.getItem('language-banner-dismissed');
    if (dismissed) {
      return;
    }

    // Show whenever the browser's language differs from what we render
    if (detectedLocale && detectedLocale !== currentLocale) {
      setIsVisible(true);
    }
  }, [currentLocale, detectedLocale]);

  const handleSwitch = () => {
    if (detectedLocale) {
      // Save user preference (with proper domain for subdomain sharing)
      setLocaleCookie(detectedLocale);
      // Reload page to apply new locale
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    // Remember that user dismissed the banner
    localStorage.setItem('language-banner-dismissed', 'true');
  };

  if (!isVisible || !detectedLocale) {
    return null;
  }

  const locale = getLocaleByCode(detectedLocale);
  if (!locale) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-teal-600 text-white shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
          <div className="flex-1 flex items-start justify-between gap-2">
            <p className="text-card-body flex-1">
              {t('message', { language: locale.nativeName })}
            </p>
            <button
              onClick={handleDismiss}
              className="p-1 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 rounded-md transition-colors flex-shrink-0 md:hidden"
              aria-label={tCommon('close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSwitch}
              className="flex-1 px-4 py-2 bg-white text-teal-600 rounded-md text-list-item-value hover:bg-teal-50 transition-colors whitespace-nowrap md:flex-none"
            >
              {t('switch', { language: locale.nativeName })}
            </button>
            <button
              onClick={handleDismiss}
              className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-md text-list-item-value hover:bg-teal-800 transition-colors whitespace-nowrap md:flex-none"
            >
              {t('dismiss')}
            </button>
            <button
              onClick={handleDismiss}
              className="hidden md:block p-2 bg-teal-600 hover:bg-teal-700 active:bg-teal-800 rounded-md transition-colors"
              aria-label={tCommon('close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
